import { Controller } from "@hotwired/stimulus";

/**
 * SharePoint Import Form Controller (GR-2416)
 *
 * Two guardrails on the historical-booking upload form:
 *
 *  - Size check: the full export is ~74MB raw and must be compressed (a .zip
 *    or gzipped .csv.gz is a few MB). Raw files over the server cap (and Cloud
 *    Run's ~32MB request limit) otherwise fail with an opaque 413. We check the
 *    chosen file's size up front, show a "compress it" message, and disable the
 *    submit button so the doomed POST is never sent.
 *
 *  - Confirm: a real (non-dry-run) import creates records, so we ask for
 *    confirmation on submit unless the dry-run box is ticked.
 *
 * The dry-run checkbox and submit button are looked up by query rather than
 * Stimulus targets so the Primer view components don't need extra data wiring.
 */
export class SharepointImportFormController extends Controller {
  static targets = ["sizeError"];
  static values = { maxBytes: Number };

  connect() {
    this.validateSize();
  }

  get fileInput() {
    return this.element.querySelector('input[type="file"]');
  }

  get submitButton() {
    return this.element.querySelector('[type="submit"]');
  }

  get dryRunChecked() {
    // Primer renders a hidden input plus the checkbox, both named "dry_run",
    // so target the checkbox specifically.
    const checkbox = this.element.querySelector('input[type="checkbox"][name="dry_run"]');
    return Boolean(checkbox && checkbox.checked);
  }

  get selectedFile() {
    const input = this.fileInput;
    return input && input.files && input.files[0];
  }

  get tooBig() {
    const file = this.selectedFile;
    return Boolean(file && this.maxBytesValue && file.size > this.maxBytesValue);
  }

  validateSize() {
    if (this.hasSizeErrorTarget) this.sizeErrorTarget.hidden = !this.tooBig;
    if (this.submitButton) this.submitButton.disabled = this.tooBig;
  }

  submit(event) {
    if (this.tooBig) {
      event.preventDefault();
      this.validateSize();
      return;
    }

    if (!this.dryRunChecked && !window.confirm(this.confirmMessage)) {
      event.preventDefault();
    }
  }

  get confirmMessage() {
    return (
      "Import bookings now? This creates event requests for every new booking in the file. " +
      "Already-imported bookings are skipped. Run a dry run first if you want to preview the result."
    );
  }
}
